import React, { useState } from 'react';
import { Modal, Table, Button, Form } from 'react-bootstrap';
import { fmt } from '../../../../../helper/storeQuotation';
import { CheckCircle, Award, FileText, Users, Package, ArrowDown } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { finalizeQuotation } from '../../../../../Store/Store/StoreQuotation/finalizeQuotation';

const FinalizeModal = ({ quotation, onRefresh, onClose }) => {
  const [mode, setMode] = useState("SINGLE_PARTY");
  const [selectedParty, setSelectedParty] = useState("");
  const [itemSelections, setItemSelections] = useState({});
  const [remark, setRemark] = useState("");
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const partiesWithRates = quotation.parties
    .filter(p => p.party_status === "rate_received")
    .map(p => ({
      ...p,
      pId: typeof p.party_id === 'object' ? p.party_id?._id : p.party_id
    }));

  const setItemParty = (itemId, partyId) => setItemSelections(s => ({ ...s, [itemId]: partyId }));

  const getBestPartyForItem = (item) => {
    if (!item.party_rates || item.party_rates.length === 0) return null;
    return item.party_rates.reduce((best, current) => {
      const currentAmount = current.net_amount || current.amount;
      const bestAmount = best ? (best.net_amount || best.amount) : Infinity;
      return currentAmount < bestAmount ? current : best;
    }, null);
  };

  const selectedPartyData = partiesWithRates.find(p => p.pId === selectedParty);

  const handleConfirm = async () => {
    setLoading(true);
    let payload = {
      finalize_mode: mode,
      finalization_remark: remark
    };

    if (mode === "SINGLE_PARTY") {
      payload.party_id = selectedParty;
      payload.finalized_by = localStorage?.getItem("PAY_USER_ID");
    } else {
      payload.finalized_by = localStorage?.getItem("PAY_USER_ID");
      payload.item_finalizations = Object.entries(itemSelections).map(([itemId, partyId]) => {
        const item = quotation.items.find(it => it._id === itemId);
        const party = partiesWithRates.find(p => p.pId === partyId);
        const rateData = item?.party_rates?.find(r => {
          const rId = typeof r.party_id === 'object' ? r.party_id?._id : r.party_id;
          return rId === partyId;
        });
        return {
          item_main_id: itemId,
          party_id: partyId,
          remark: `${party?.party_snapshot?.name} — allocated for ${item?.item_name} at ${fmt(rateData?.net_amount || rateData?.amount)}`
        };
      });
    }

    try {
      const result = await dispatch(finalizeQuotation({ id: quotation._id, payload }));
      if (result?.payload?.success) {
        if (onRefresh) onRefresh();
        onClose();
      }
    } catch (error) {
      console.error("Finalize error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={true}
      onHide={onClose}
      size="lg"
      centered
      backdrop="static"
      contentClassName="border-0 shadow-lg rounded-4 overflow-hidden"
    >
      <Modal.Header closeButton className="border-bottom-0 pt-4 px-4 pb-2">
        <div className="d-flex align-items-center gap-3">
          <div className="p-2 rounded-3 text-primary" style={{ backgroundColor: '#ffedd7ff' }}>
            <Award size={24} />
          </div>
          <div>
            <Modal.Title className="fw-bold text-primary h5 mb-0">
              Finalize Quotation
            </Modal.Title>
            <div className="text-muted" style={{ fontSize: '12px' }}>
              Step 4 — Select the winning party/parties to award the contract
            </div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="px-4 pb-3">
        {/* Mode Selection */}
        <div className="row g-2 mb-4 mt-2">
          <div className="col-6">
            <button
              className={`w-100 btn text-start p-3 border-2 transition-all d-flex flex-column gap-1 ${mode === 'SINGLE_PARTY' ? 'active shadow-sm' : 'btn-outline-light text-muted border-light-subtle'}`}
              style={{
                borderRadius: '12px',
                borderColor: mode === 'SINGLE_PARTY' ? '#ff6700' : '#e2e8f0',
                backgroundColor: mode === 'SINGLE_PARTY' ? '#fff7ed' : '#fff',
                color: mode === 'SINGLE_PARTY' ? '#ff6700' : 'inherit'
              }}
              onClick={() => setMode("SINGLE_PARTY")}
            >
              <div className="d-flex align-items-center gap-2 fw-bold">
                <Users size={16} />
                <span>Single Party</span>
              </div>
              <small className="opacity-75 fw-normal" style={{ fontSize: '11px' }}>Award all items to one supplier</small>
            </button>
          </div>
          <div className="col-6">
            <button
              className={`w-100 btn text-start p-3 border-2 transition-all d-flex flex-column gap-1 ${mode === 'PER_ITEM' ? 'active shadow-sm' : 'btn-outline-light text-muted border-light-subtle'}`}
              style={{
                borderRadius: '12px',
                borderColor: mode === 'PER_ITEM' ? '#ff6700' : '#e2e8f0',
                backgroundColor: mode === 'PER_ITEM' ? '#fff7ed' : '#fff',
                color: mode === 'PER_ITEM' ? '#ff6700' : 'inherit'
              }}
              onClick={() => setMode("PER_ITEM")}
            >
              <div className="d-flex align-items-center gap-2 fw-bold">
                <Package size={16} />
                <span>Per Item</span>
              </div>
              <small className="opacity-75 fw-normal" style={{ fontSize: '11px' }}>Split order by best item prices</small>
            </button>
          </div>
        </div>

        {/* SINGLE PARTY SELECTION */}
        {mode === "SINGLE_PARTY" && (
          <div className="mb-3">
            <label className="small fw-bold  text-muted mb-2 d-block" style={{ letterSpacing: '0.05em' }}>
              Select Winning Supplier
            </label>
            <div className="d-flex flex-column gap-2 overflow-auto pe-1" style={{ maxHeight: '400px' }}>
              {partiesWithRates.map(p => {
                const isSelected = selectedParty === p.pId;
                return (
                  <div
                    key={p.pId}
                    onClick={() => setSelectedParty(p.pId)}
                    className={`p-3 rounded-3 border d-flex justify-content-between align-items-center transition-all ${isSelected ? 'border-primary' : 'bg-white border-light-subtle'}`}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#fff7ed' : '#fff',
                      borderColor: isSelected ? '#ff6700' : '#f1f5f9',
                      borderWidth: '1.5px',
                    }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <div className={`p-1 rounded-circle border d-flex align-items-center justify-content-center ${isSelected ? 'bg-primary border-primary text-white' : 'border-secondary-subtle bg-white'}`} style={{ width: '20px', height: '20px' }}>
                        {isSelected && <div style={{ width: '8px', height: '8px', backgroundColor: '#fff', borderRadius: '50%' }} />}
                      </div>
                      <div>
                        <div className={`fw-bold mb-0 ${isSelected ? 'text-primary' : '#1e293b'}`} style={{ fontSize: '14px' }}>
                          {p.party_snapshot?.name}
                        </div>
                        <div className="text-muted" style={{ fontSize: '11px' }}>
                          {p.delivery_terms && `${p.delivery_terms}`} • {p.payment_terms && ` ${p.payment_terms}`}
                        </div>
                      </div>
                    </div>
                    <div className="text-end">
                      <div className={`fw-bold ${isSelected ? 'text-primary' : 'text-dark'}`} style={{ fontSize: '15px' }}>
                        {fmt(p.total_net_amount)}
                      </div>
                      <div className="text-muted" style={{ fontSize: '10px' }}>Net Amount</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* PER ITEM SELECTION */}
        {mode === "PER_ITEM" && (
          <div className="mb-3">
            <label className="small fw-bold text-muted mb-2 d-block" style={{ letterSpacing: '0.05em' }}>
              Item Wise Allocation
            </label>
            <div className="table-responsive border rounded-3 overflow-hidden bg-white">
              <Table hover size="sm" className="mb-0 border-0" style={{ fontSize: '13px' }}>
                <thead className="bg-light border-bottom">
                  <tr>
                    <th className="ps-3 py-2 fw-bold text-muted" style={{ fontSize: '11px' }}>Item</th>
                    <th className="text-center py-2 fw-bold text-muted" style={{ fontSize: '11px' }}>Qty</th>
                    {partiesWithRates?.map(p => (
                      <th key={p.pId} className="text-center py-2 fw-bold text-muted" style={{ fontSize: '11px' }}>
                        {p.party_snapshot?.name}
                      </th>
                    ))}
                    <th className="pe-3 py-2 fw-bold text-muted" style={{ fontSize: '11px', width: '160px' }}>Select Winner</th>
                  </tr>
                </thead>
                <tbody>
                  {quotation.items.map(item => (
                    <tr key={item._id} className="align-middle border-bottom">
                      <td className="ps-3 py-2">
                        <div className="fw-semibold text-dark">{item.item_name}</div>
                      </td>
                      <td className="text-center py-2">
                        {fmt(item.quantity)}
                      </td>
                      {partiesWithRates.map(p => {
                        const r = item.party_rates?.find(x => (typeof x.party_id === 'object' ? x.party_id?._id : x.party_id) === p.pId);
                        const isBest = getBestPartyForItem(item)?.party_id === p.pId || (typeof getBestPartyForItem(item)?.party_id === 'object' && getBestPartyForItem(item)?.party_id?._id === p.pId);
                        return (
                          <td key={p.pId} className="text-center py-2">
                            {r ? (
                              <div className="d-flex flex-column align-items-center">
                                <span className={`fw-bold d-flex align-items-center gap-1 ${isBest ? 'text-success' : 'text-muted'}`} style={{ fontSize: '12px' }}>
                                  {fmt(r.net_amount || r.amount)} {isBest && <ArrowDown strokeWidth={4} size={12} />}
                                </span>
                              </div>
                            ) : (
                              <span className="text-light-emphasis opacity-25">-</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="pe-3 py-2 text-end">
                        <Form.Select
                          size="sm"
                          className="border-light-subtle shadow-none bg-light-subtle"
                          style={{ fontSize: '11px', borderRadius: '6px' }}
                          value={itemSelections[item._id] || ""}
                          onChange={e => setItemParty(item._id, e.target.value)}
                        >
                          <option value="">Select Party</option>
                          {partiesWithRates.map(p => (
                            <option key={p.pId} value={p.pId}>
                              {p.party_snapshot?.name}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}

        {/* REMARK */}
        <div className="mt-4">
          <label className="small fw-bold text-muted mb-2 d-flex align-items-center gap-2" style={{ letterSpacing: '0.05em' }}>
            <FileText size={14} /> Finalize Remarks
          </label>
          <Form.Control
            as="textarea"
            rows={2}
            className="border-light-subtle rounded-3 shadow-none bg-light bg-opacity-10"
            style={{ fontSize: '13px' }}
            value={remark}
            onChange={e => setRemark(e.target.value)}
            placeholder="Reason for selection..."
          />
        </div>
      </Modal.Body>

      <Modal.Footer className="bg-light bg-opacity-50 border-top-0 px-4 py-3 gap-2">
        <button
          className="rounded-3 btn btn-secondary px-4 py-2 fw-bold d-flex align-items-center gap-2"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          className="rounded-3 btn btn-primary px-4 py-2 fw-bold d-flex align-items-center gap-2"
          onClick={handleConfirm}
          disabled={loading || (mode === "SINGLE_PARTY" ? !selectedParty : Object.keys(itemSelections).length === 0)}
        >
          {loading ? 'Finalizing...' : (
            <>
              <CheckCircle size={16} />
              Confirm Finalize
            </>
          )}
        </button>
      </Modal.Footer>

      <style>{`
        .transition-all { transition: all 0.2s ease-in-out; }
        .transition-all:hover { border: 1px solid #ff6700 !important; background-color: #fff7ed !important; }
        .form-select:focus { border-color: #ff6700; box-shadow: 0 0 0 0.2rem rgba(255, 103, 0, 0.1); }
      `}</style>
    </Modal>
  );
};

export default FinalizeModal;